import { useEffect, useState } from "react";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PieceRateReport } from "../../components/PieceRateReport";
import { MyEarnings } from "../../components/MyEarnings";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type TimeEntry } from "@clevis/shared-types";
import { Button, Card, CardTitle } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { currentPosition } from "../../lib/geo";
import { useAuth } from "../../hooks/useAuth";
import { useLocationSharing } from "../../hooks/useLocationSharing";

function sharePref(): boolean {
  try {
    return localStorage.getItem("clevis.shareLocation") !== "0";
  } catch {
    return true;
  }
}

export function TimeClock() {
  const { tenantId, user } = useAuth();
  const { hasFeature } = useEntitlements();
  const [entries, setEntries] = useState<Array<TimeEntry & { id: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [share, setShare] = useState(sharePref);

  useEffect(() => {
    if (!tenantId || !user) return;
    const q = query(
      collection(db, paths.tenantTimeEntries(tenantId)),
      where("uid", "==", user.uid),
      orderBy("clockInAt", "desc"),
      limit(14),
    );
    return onSnapshot(q, (snap) => {
      setEntries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TimeEntry) })));
    });
  }, [tenantId, user]);

  const openEntry = entries.find((e) => e.status === "open");
  // While clocked in (and opted in), the office's live map sees this tech.
  useLocationSharing(tenantId, !!openEntry && share);

  function toggleShare() {
    const next = !share;
    setShare(next);
    try {
      localStorage.setItem("clevis.shareLocation", next ? "1" : "0");
    } catch {
      /* private mode */
    }
  }

  async function punch() {
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      const geo = await currentPosition();
      if (openEntry) {
        await call("clockOut", { tenantId, geo });
      } else {
        await call("clockIn", { tenantId, geo });
      }
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function fmt(ms: number) {
    return new Date(ms).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-lg font-bold">Time Clock</h1>
      {tenantId ? <MyEarnings tenantId={tenantId} /> : null}
      {tenantId && hasFeature("pieceRate") ? <PieceRateReport tenantId={tenantId} mine /> : null}
      <Card className="text-center">
        <p className="mb-3 text-sm text-steel">
          {openEntry ? `Clocked in since ${fmt(openEntry.clockInAt)}` : "You're clocked out."}
        </p>
        <Button onClick={punch} busy={busy} variant={openEntry ? "secondary" : "primary"} className="w-full py-3 text-base">
          {openEntry ? "Clock out" : "Clock in"}
        </Button>
        {message ? <p className="mt-2 text-xs text-danger">{message}</p> : null}
        <p className="mt-2 text-[11px] text-steel">Your location is stamped on each punch.</p>
        {openEntry ? (
          <label className="mt-2 flex items-center justify-center gap-2 text-xs text-ink">
            <input type="checkbox" checked={share} onChange={toggleShare} />
            Share live location with dispatch while clocked in
          </label>
        ) : null}
      </Card>
      <Card className="p-0">
        <CardTitle className="px-4 pt-3">Recent entries</CardTitle>
        {entries.map((e) => (
          <div key={e.id} className="flex items-center justify-between border-b border-steel/10 px-4 py-2 text-sm last:border-0">
            <div>
              <div className="text-ink">{fmt(e.clockInAt)}</div>
              <div className="text-xs text-steel">
                {e.clockOutAt ? `→ ${fmt(e.clockOutAt)}` : "still open"}
              </div>
            </div>
            <span className="rounded-full bg-steel/15 px-2 py-0.5 text-xs capitalize">{e.status}</span>
          </div>
        ))}
        {entries.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-steel">No entries yet.</div>
        ) : null}
      </Card>
    </div>
  );
}
